import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { toast } from 'sonner'

export default function SalesLeads() {
  const [leads, setLeads] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const { user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    fetchLeads()
  }, [statusFilter])

  async function fetchLeads() {
    try {
      const data = await api.getLeads()
      let filteredData = data

      if (statusFilter !== 'all') {
        filteredData = data.filter(lead => lead.status === statusFilter)
      }

      setLeads(filteredData)
    } catch (error) {
      console.error('Fehler beim Laden der Leads:', error)
      toast.error('Leads konnten nicht geladen werden')
    }
  }

  const filteredLeads = leads.filter(lead =>
    lead.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.notes?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-yellow-200'
      case 'approved': return 'bg-green-200'
      case 'completed': return 'bg-blue-200'
      case 'rejected': return 'bg-red-100'
      default: return ''
    }
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Leads</h1>
        <Button onClick={() => navigate('/leads/new')}>
          Neuer Lead
        </Button>
      </div>

      <div className="flex gap-4 mb-6">
        <Input
          placeholder="Suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="max-w-sm"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="new">Neu</SelectItem>
            <SelectItem value="in_progress">In Bearbeitung</SelectItem>
            <SelectItem value="approved">Freigegeben</SelectItem>
            <SelectItem value="completed">Abschluss</SelectItem>
            <SelectItem value="rejected">Abgebrochen</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Datum</TableHead>
            <TableHead>Kunde</TableHead>
            <TableHead>Status</TableHead>
            {user.role === 'admin' && <TableHead>Vertriebler</TableHead>}
            <TableHead>Aktionen</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredLeads.map((lead) => (
            <TableRow key={lead.id}>
              <TableCell>
                {format(new Date(lead.created_at), 'dd.MM.yyyy', { locale: de })}
              </TableCell>
              <TableCell>{lead.customer_name}</TableCell>
              <TableCell>
                <span className={`px-2 py-1 rounded ${getStatusColor(lead.status)}`}>
                  {lead.status === 'new' && 'Neu'}
                  {lead.status === 'in_progress' && 'In Bearbeitung'}
                  {lead.status === 'approved' && 'Freigegeben'}
                  {lead.status === 'completed' && 'Abschluss'}
                  {lead.status === 'rejected' && 'Abgebrochen'}
                </span>
              </TableCell>
              {user.role === 'admin' && (
                <TableCell>
                  {lead.sales_rep_name || lead.sales_rep_email || (
                    <span className="text-gray-500 italic">Nicht zugewiesen</span>
                  )}
                  {lead.sales_rep_role === 'admin' && (
                    <span className="ml-1 text-xs text-gray-500">(Admin)</span>
                  )}
                </TableCell>
              )}
              <TableCell>
                <Button 
                  variant="outline" 
                  onClick={() => navigate(`/leads/${lead.id}`)}
                >
                  Details
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
} 